import {
  Box,
  Drawer,
  Paper,
  useMediaQuery,
  useTheme,
} from '@locus-taxy/encore-mui';
import { GenericErrorPage } from '@locus-taxy/encore-ui-toolkit';
import WorkflowTimelineContainer from './WorkflowTimelineContainer';
import EntityDetailsForm from './EntityDetailsForm';
import type { EntityDetailsBodyProps } from './types';
import { TourDetailsContainer } from './TourDetailsContainer';
import LoadingSpinner from '../LoadingSpinner';
import { CustomDetailsPageContainer } from '../../customApps/CustomDetailsPageContainer';
import { getCustomDetailsConfig } from '../../customApps/customDetailsConfig';
import { getTestId } from '@locus-taxy/encore-utils';
import { TestIdAction } from '../../utils/testIdUtils';

const DETAILS_BODY_TEST_ID = 'details-body';

function renderDetailsSection({
  entityName,
  entityTitle,
  rootInstanceKey,
  entityData,
  detailsForm,
  showTimeline,
  mfeRemountKey,
  entitySchemaInfo,
  resolvedClientId,
}: Readonly<
  Pick<
    EntityDetailsBodyProps,
    | 'entityName'
    | 'entityTitle'
    | 'rootInstanceKey'
    | 'entityData'
    | 'detailsForm'
    | 'showTimeline'
    | 'mfeRemountKey'
    | 'entitySchemaInfo'
  > & { resolvedClientId: string }
>) {
  if (entityName === 'b2cDeliveryTour') {
    return (
      <TourDetailsContainer
        entityData={entityData}
        remountKey={mfeRemountKey}
      />
    );
  }

  const customDetailsConfig = getCustomDetailsConfig(entityName);

  if (customDetailsConfig) {
    return (
      <CustomDetailsPageContainer
        config={customDetailsConfig}
        clientId={resolvedClientId}
        entityName={entityName}
        entityTitle={entityTitle}
        rootInstanceKey={rootInstanceKey}
        entityData={entityData}
        detailsForm={detailsForm}
        showTimeline={showTimeline}
        remountKey={mfeRemountKey}
        entitySchemaInfo={entitySchemaInfo}
      />
    );
  }

  return (
    <EntityDetailsForm
      entityName={entityName}
      entityData={entityData}
      detailsForm={detailsForm}
      isLoading={false}
      error={null}
    />
  );
}

function renderTimelineDrawer({
  isXs,
  showTimeline,
  onTimelineClose,
  timelineContent,
}: Readonly<{
  isXs: boolean;
  showTimeline: boolean | undefined;
  onTimelineClose: EntityDetailsBodyProps['onTimelineClose'];
  timelineContent: React.ReactNode;
}>) {
  if (!timelineContent) {
    return null;
  }

  return (
    <Drawer
      anchor={isXs ? 'bottom' : 'right'}
      open={Boolean(showTimeline)}
      onClose={() => onTimelineClose?.()}
      ModalProps={{ keepMounted: true }}
    >
      <Box
        sx={{
          zIndex: 2,
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          p: 2,
          gap: 2,
        }}
      >
        <Paper variant="outlined" sx={{ flex: 1, overflow: 'hidden' }}>
          {timelineContent}
        </Paper>
      </Box>
    </Drawer>
  );
}

function renderTimelineAside(timelineContent: React.ReactNode) {
  if (!timelineContent) {
    return null;
  }

  return (
    <Box borderLeft="1px solid" borderColor="divider" overflow="hidden">
      {timelineContent}
    </Box>
  );
}

function renderGridLayout({
  entityName,
  detailsSection,
  timelineContent,
}: Readonly<{
  entityName: EntityDetailsBodyProps['entityName'];
  detailsSection: React.ReactNode;
  timelineContent: React.ReactNode;
}>) {
  return (
    <Box
      data-testid={getTestId({
        action: TestIdAction.VIEW,
        object: DETAILS_BODY_TEST_ID,
        entity: entityName,
        page: 'details',
      })}
      sx={{
        width: '100%',
        height: '100%',
        overflow: 'hidden',
        display: 'grid',
        gridTemplateColumns: timelineContent ? '7fr 3fr' : '1fr',
      }}
    >
      <Box sx={{ overflow: 'auto' }}>{detailsSection}</Box>
      {renderTimelineAside(timelineContent)}
    </Box>
  );
}

function renderStackLayout({
  entityName,
  detailsSection,
}: Readonly<{
  entityName: EntityDetailsBodyProps['entityName'];
  detailsSection: React.ReactNode;
}>) {
  return (
    <Box
      data-testid={getTestId({
        action: TestIdAction.VIEW,
        object: DETAILS_BODY_TEST_ID,
        entity: entityName,
        page: 'details',
      })}
      sx={{
        width: '100%',
        height: '100%',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box sx={{ overflow: 'auto' }}>{detailsSection}</Box>
    </Box>
  );
}

function renderStackedTimelineDrawer({
  hasToggleControl,
  isXs,
  showTimeline,
  onTimelineClose,
  timelineContent,
}: Readonly<{
  hasToggleControl: boolean;
  isXs: boolean;
  showTimeline: boolean | undefined;
  onTimelineClose: EntityDetailsBodyProps['onTimelineClose'];
  timelineContent: React.ReactNode;
}>) {
  if (!hasToggleControl) {
    return null;
  }

  return renderTimelineDrawer({
    isXs,
    showTimeline,
    onTimelineClose,
    timelineContent,
  });
}

function getResolvedClientId(
  clientId: EntityDetailsBodyProps['clientId'],
  entityData: EntityDetailsBodyProps['entityData']
) {
  return clientId ?? (entityData?.clientId as string | undefined) ?? '';
}

function getTimelineContent({
  rootInstanceKey,
  shouldRenderTimeline,
  resolvedClientId,
  entityName,
  entityTitle,
}: Readonly<{
  rootInstanceKey: EntityDetailsBodyProps['rootInstanceKey'];
  shouldRenderTimeline: boolean;
  resolvedClientId: string;
  entityName: EntityDetailsBodyProps['entityName'];
  entityTitle: EntityDetailsBodyProps['entityTitle'];
}>) {
  if (!rootInstanceKey || !shouldRenderTimeline) {
    return null;
  }

  return (
    <WorkflowTimelineContainer
      clientId={resolvedClientId}
      entityName={entityName}
      rootInstanceKey={rootInstanceKey}
      title={`${entityTitle} Timeline`}
    />
  );
}

function EntityDetailsBodyContent({
  clientId,
  entityName,
  entityTitle,
  rootInstanceKey,
  entityData,
  detailsForm,
  showTimeline,
  onTimelineClose,
  mfeRemountKey,
  entitySchemaInfo,
}: Readonly<EntityDetailsBodyProps>) {
  const theme = useTheme();
  const isMdUp = useMediaQuery(theme.breakpoints.up('lg'));
  const isXs = useMediaQuery(theme.breakpoints.only('xs'));
  const hasToggleControl = typeof showTimeline === 'boolean';
  const resolvedShowTimeline = hasToggleControl ? showTimeline : true;
  const shouldRenderTimeline =
    Boolean(rootInstanceKey) && Boolean(resolvedShowTimeline);
  const useGridLayout = isMdUp || !hasToggleControl;
  const resolvedClientId = getResolvedClientId(clientId, entityData);
  const timelineContent = getTimelineContent({
    rootInstanceKey,
    shouldRenderTimeline,
    resolvedClientId,
    entityName,
    entityTitle,
  });
  const detailsSection = renderDetailsSection({
    entityName,
    entityTitle,
    rootInstanceKey,
    entityData,
    detailsForm,
    showTimeline,
    mfeRemountKey,
    entitySchemaInfo,
    resolvedClientId,
  });
  const layoutContent = useGridLayout
    ? renderGridLayout({
        entityName,
        detailsSection,
        timelineContent,
      })
    : renderStackLayout({
        entityName,
        detailsSection,
      });

  return (
    <>
      {layoutContent}
      {!useGridLayout
        ? renderStackedTimelineDrawer({
            hasToggleControl,
            isXs,
            showTimeline,
            onTimelineClose,
            timelineContent,
          })
        : null}
    </>
  );
}

export default function EntityDetailsBody({
  clientId,
  entityName,
  entityTitle,
  rootInstanceKey,
  entityData,
  detailsForm,
  isLoading,
  error,
  showTimeline,
  onTimelineClose,
  mfeRemountKey,
  entitySchemaInfo,
}: Readonly<EntityDetailsBodyProps>) {
  if (isLoading) {
    return (
      <Box sx={{ flex: 1, display: 'flex', minHeight: 0 }}>
        <LoadingSpinner />
      </Box>
    );
  }

  if (error) {
    return <GenericErrorPage />;
  }

  return (
    <EntityDetailsBodyContent
      clientId={clientId}
      entityName={entityName}
      entityTitle={entityTitle}
      rootInstanceKey={rootInstanceKey}
      entityData={entityData}
      detailsForm={detailsForm}
      isLoading={isLoading}
      error={error}
      showTimeline={showTimeline}
      onTimelineClose={onTimelineClose}
      mfeRemountKey={mfeRemountKey}
      entitySchemaInfo={entitySchemaInfo}
    />
  );
}
